import React from 'react';
import { css, styled } from '@this/constants/themes';
import type SegmentClass from '@this/src/domain/transit/segment';
import type SelectStore from '@this/src/domain/select_store';
import type Transit from '@this/src/domain/transit/transit';
import PackageRouteInfo from './PackageRouteInfo';
import { BoxButtonText, Yen } from '../../../shared/box_parts';
import SelectTransportBoxPriceRangeLabel from '../../price_range_label';

interface PackageDetailProps {
  transport: Transit;
  selected: boolean;
  handleChange: () => void;
  store: SelectStore;
  segment: SegmentClass;
  segmentIdx: number;
  isRequestForm?: boolean;
}

const PackageDetail = ({
  transport,
  selected,
  handleChange,
  store,
  isRequestForm,
  segment,
  segmentIdx
}: PackageDetailProps) => {
  const isAirPackage = store.result.type === 'airPackage';
  const hasAir = transport.airExists();

  const renderPackageRightPanel = () => {
    const price = transport.price;
    const priceRange = isAirPackage ? transport.price_range : undefined;
    const includeTax = transport.includeTax;
    const showsPricePerPersonText = !isAirPackage;
    const showsIncludeTax = !isAirPackage;
    const isRakutenPackage = transport.isRakutenPackage;

    return (
      <StyledPackageBoxWrap isRakutenPackage={isRakutenPackage === true}>
        {!isRakutenPackage && (
          <StyledPackageBoxPriceArea>
            <StyledPackageBoxPrice>
              {price !== undefined && (
                <>
                  <span data-wovn-ignore>{utils.digits(price)}</span>
                  <Yen>円</Yen>
                </>
              )}
              {priceRange && <SelectTransportBoxPriceRangeLabel priceRange={priceRange} />}
            </StyledPackageBoxPrice>
            <StyledPackageBoxSmall>
              {showsPricePerPersonText ? '/1名' : ''}
              {showsIncludeTax ? (includeTax ? '(税込)' : '(税別)') : ''}
            </StyledPackageBoxSmall>
          </StyledPackageBoxPriceArea>
        )}
        {isRequestForm === false && (
          <StyledPackageBoxButtonArea>
            <StyledPackageBoxButton selected={selected} onClick={handleChange}>
              <BoxButtonText className="select-transport-box__right-gray__button-text">
                {selected ? (
                  <span>
                    変更を
                    <br />
                    キャンセル
                  </span>
                ) : (
                  '変更 >'
                )}
              </BoxButtonText>
            </StyledPackageBoxButton>
          </StyledPackageBoxButtonArea>
        )}
      </StyledPackageBoxWrap>
    );
  };

  return (
    <div className="select-transport-box__button_exist_leg_shortdistance">
      <StyledLeft>
        {segment.legs.map((leg, i) => (
          <StyledSegment key={`package_leg_short_distance_${leg.text}`}>
            <PackageRouteInfo leg={leg} legIndex={i} segment={segment} segmentIdx={segmentIdx} hasAir={hasAir} />
          </StyledSegment>
        ))}
      </StyledLeft>
      {renderPackageRightPanel()}
    </div>
  );
};

const StyledLeft = styled.div`
  flex-shrink: 10;
  width: 100%;
`;

const StyledSegment = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
`;

const StyledPackageBoxWrap = styled.div<{ isRakutenPackage?: boolean }>`
  display: flex;
  flex-grow: 0;
  margin-left: auto;
  min-width: 170px;
  color: ${props => (props.theme.themeClass === 'tabikobo' ? props.theme.textColor : props.theme.linkColor)};
`;

const StyledPackageBoxPriceArea = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: auto 0 auto 10px;
`;

const StyledPackageBoxPrice = styled.span`
  color: ${props => (props.theme.themeClass === 'tabikobo' ? props.theme.textColor : props.theme.moneyColor)};
  font-size: 14px;
  font-weight: bold;
  display: block;
`;

const StyledPackageBoxSmall = styled.div`
  font-size: 10px;
  display: inline-block;
`;

const StyledPackageBoxButtonArea = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: auto 13px auto auto;
`;

const StyledPackageBoxButton = styled.div<{ selected: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;

  width: 53px;
  height: 30px;
  text-align: center;
  color: ${props => props.theme.linkColor};
  background: white;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  padding: auto 2px;
  font-weight: bold;
  line-height: 11px;
  font-size: 11px;
  cursor: pointer;

  ${props =>
    props.selected &&
    `
    background: ${props.theme.linkColor};
    color: white;
    font-size: 9px;
    letter-spacing: -1px;
  `};

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.6;
          pointer-events: none;
        `
      : null}
`;

export default PackageDetail;
